/**
 * Activity Feed Component
 *
 * Shows a real-time stream of events from the planning workflow.
 */

import { useEffect, useRef } from 'react';

export interface ActivityEvent {
  id: string;
  timestamp: Date;
  type: string;
  message: string;
  status?: 'info' | 'success' | 'warning' | 'error' | 'progress';
  details?: Record<string, unknown>;
}

interface ActivityFeedProps {
  events: ActivityEvent[];
  maxHeight?: string;
  autoScroll?: boolean;
  /** Show as full height panel */
  fullHeight?: boolean;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function StatusIcon({ status }: { status?: ActivityEvent['status'] }) {
  switch (status) {
    case 'success':
      return (
        <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      );
    case 'error':
      return (
        <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      );
    case 'warning':
      return (
        <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      );
    case 'progress':
      return (
        <div className="w-4 h-4 flex items-center justify-center">
          <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      );
    default:
      return (
        <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      );
  }
}

export function ActivityFeed({ events, maxHeight = '400px', autoScroll = true, fullHeight = false }: ActivityFeedProps) {
  const feedRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new events arrive
  useEffect(() => {
    if (autoScroll && feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [events, autoScroll]);

  // Full height mode for sidebar column layout
  if (fullHeight) {
    return (
      <div className="flex flex-col h-full bg-gray-800">
        <div className="px-4 py-2 border-b border-gray-700 flex items-center justify-between shrink-0">
          <h3 className="text-sm font-medium text-gray-200">Activity</h3>
          <span className="text-xs text-gray-500">{events.length} events</span>
        </div>

        <div
          ref={feedRef}
          className="flex-1 overflow-y-auto"
        >
          {events.length === 0 ? (
            <div className="py-8 text-center text-gray-500 text-sm">
              No activity yet
            </div>
          ) : (
            <div className="divide-y divide-gray-700/50">
              {events.map(event => (
                <div
                  key={event.id}
                  className="px-4 py-2 flex items-start gap-3 hover:bg-gray-750/50 transition-colors"
                >
                  <span className="text-xs text-gray-500 font-mono whitespace-nowrap pt-0.5">
                    {formatTime(event.timestamp)}
                  </span>
                  <div className="pt-0.5">
                    <StatusIcon status={event.status} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${
                      event.status === 'error' ? 'text-red-400' :
                      event.status === 'warning' ? 'text-yellow-400' :
                      event.status === 'success' ? 'text-green-400' :
                      'text-gray-300'
                    }`}>
                      {event.message}
                    </p>
                    <span className="text-xs text-gray-500">
                      {event.type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
      <div className="px-4 py-2 border-b border-gray-700 flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-200">Activity</h3>
        <span className="text-xs text-gray-500">{events.length} events</span>
      </div>

      <div
        ref={feedRef}
        className="overflow-y-auto"
        style={{ maxHeight }}
      >
        {events.length === 0 ? (
          <div className="py-8 text-center text-gray-500 text-sm">
            No activity yet
          </div>
        ) : (
          <div className="divide-y divide-gray-700/50">
            {events.map(event => (
              <div
                key={event.id}
                className="px-4 py-2 flex items-start gap-3 hover:bg-gray-750/50 transition-colors"
              >
                {/* Timestamp */}
                <span className="text-xs text-gray-500 font-mono whitespace-nowrap pt-0.5">
                  {formatTime(event.timestamp)}
                </span>

                {/* Status icon */}
                <div className="pt-0.5">
                  <StatusIcon status={event.status} />
                </div>

                {/* Message */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${
                    event.status === 'error' ? 'text-red-400' :
                    event.status === 'warning' ? 'text-yellow-400' :
                    event.status === 'success' ? 'text-green-400' :
                    'text-gray-300'
                  }`}>
                    {event.message}
                  </p>

                  {/* Event type badge */}
                  <span className="text-xs text-gray-500">
                    {event.type}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
